import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { deleteShareLink, getSharedConversation, resolveSharedFileUrl } from '../../api/chatApi';
import EnhancedMarkdown from '../../components/EnhancedMarkdown';
import { answerTextForShare } from '../../utils/chatRichCatalog';
import { stripLeakedCitationMarkers } from '../../utils/chatArtifactTags';
import {
  buildShareTryUrl,
  formatShareFileSize,
  shareFileFormatLabel,
  summarizeSharedFiles,
} from '../../utils/chatShare';
import { downloadBlobFile } from '../../utils/downloadUtils';
import { trackChatUxEvent } from '../../utils/eventTracker';
import WhatQuizElephant from '../../components/brand/WhatQuizElephant';
import './SharedChatPage.css';

const HwpDocResultViewer = lazy(() => import('../../components/events/HwpDocResultViewer'));

const CATEGORY_NAMES = {
  pro_ai: 'PRO AI',
  record: '생기부 전문가',
  document: 'AI 한글',
  lesson_design: '수업 설계',
  parent_communication: '학부모 소통',
  student_counseling: '학생 상담',
  teacher_care: '교사 멘탈 케어',
  evaluation: '평가 관리',
  general: '자유로운 AI',
  admin_docs: '행정 문서',
  worksheet_gen: '학습지 만들기',
  ppt_maker: 'PPT 만들기',
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const ShareTopbar = ({ linkHome = true }) => {
  const brand = (
    <>
      <span className="shared-chat-brand-mark">
        <WhatQuizElephant size={32} />
      </span>
      <strong>왓퀴즈</strong>
    </>
  );
  return (
    <header className="shared-chat-topbar">
      {linkHome ? (
        <Link className="shared-chat-brand" to="/" aria-label="왓퀴즈 홈">{brand}</Link>
      ) : (
        <div className="shared-chat-brand" aria-label="왓퀴즈">{brand}</div>
      )}
      <span className="shared-chat-readonly-label">공유받은 AI 대화</span>
    </header>
  );
};

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M7 3h7l5 5v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const ClipIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="m20 11.5-7.8 7.8a5 5 0 0 1-7.1-7.1l8.2-8.2a3.3 3.3 0 0 1 4.7 4.7l-8.2 8.2a1.7 1.7 0 0 1-2.4-2.4l7.4-7.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SharedFileCard = ({ artifact, busy, error, onDownload, onPreview }) => {
  const label = shareFileFormatLabel(artifact.format);
  const size = formatShareFileSize(artifact.size_bytes);
  return (
    <div className="shared-file-item">
      <div className={`shared-file-card is-${artifact.format || 'file'}`}>
        <span className="shared-file-icon" aria-hidden="true"><FileIcon /></span>
        <div className="shared-file-info">
          <strong className="shared-file-name" title={artifact.filename}>{artifact.filename}</strong>
          <span className="shared-file-meta">{[`${label} 파일`, size].filter(Boolean).join(' · ')}</span>
        </div>
        <div className="shared-file-actions">
          {artifact.preview_path && (
            <button type="button" className="shared-file-button" onClick={() => onPreview(artifact)}>
              미리보기
            </button>
          )}
          <button
            type="button"
            className="shared-file-button is-primary"
            onClick={() => onDownload(artifact)}
            disabled={busy}
            aria-label={`${artifact.filename} 저장`}
          >
            {busy ? '받는 중' : '저장'}
          </button>
        </div>
      </div>
      {error && <p className="shared-file-error" role="alert">{error}</p>}
    </div>
  );
};

/**
 * 공유받은 대화 페이지 — 누구나 볼 수 있는 읽기 전용 화면.
 * 공유자가 만든 파일은 미리보기·저장, 공유자가 올린 파일은 이름만 보인다.
 */
const SharedChatPage = () => {
  const { shareId } = useParams();

  const [sharedData, setSharedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewArtifact, setPreviewArtifact] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [fileErrors, setFileErrors] = useState({});
  const [revoked, setRevoked] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const viewTrackedRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchSharedConversation = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getSharedConversation(shareId);
        if (!cancelled) setSharedData(response);
      } catch (err) {
        console.error('공유 대화 로드 실패:', err);
        if (!cancelled) {
          setError(err.message || '공유된 대화를 불러오는 데 실패했습니다.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (shareId) {
      fetchSharedConversation();
    }

    return () => {
      cancelled = true;
    };
  }, [shareId]);

  useEffect(() => {
    if (!previewArtifact) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setPreviewArtifact(null);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [previewArtifact]);

  const messages = useMemo(() => sharedData?.messages || [], [sharedData]);
  const fileSummary = useMemo(() => summarizeSharedFiles(messages), [messages]);
  const category = sharedData?.category || 'general';

  useEffect(() => {
    if (!sharedData || viewTrackedRef.current === shareId) return;
    viewTrackedRef.current = shareId;
    trackChatUxEvent('share_page_view', {
      category,
      messageCount: messages.length,
      artifactCount: fileSummary.generated,
      fileCount: fileSummary.attached,
      entry: sharedData.can_manage ? 'owner' : 'viewer',
    });
  }, [sharedData, shareId, category, messages.length, fileSummary]);

  const handlePreview = useCallback((artifact) => {
    setNotice(null);
    setPreviewArtifact(artifact);
    trackChatUxEvent('share_file_preview', { category, format: artifact.format });
  }, [category]);

  const handleDownload = useCallback(async (artifact) => {
    if (!artifact?.download_path || downloadingId) return;
    setFileErrors((prev) => ({ ...prev, [artifact.file_id]: null }));
    setDownloadingId(artifact.file_id);
    try {
      const response = await fetch(resolveSharedFileUrl(artifact.download_path), {
        method: 'GET',
        credentials: 'omit',
        mode: 'cors',
      });
      if (!response.ok) {
        let detail = '';
        try { detail = (await response.json())?.detail; } catch (_e) { detail = ''; }
        throw Object.assign(new Error(typeof detail === 'string' && detail ? detail : '파일을 받지 못했어요. 잠시 후 다시 시도해주세요.'), { status: response.status });
      }
      const blob = await response.blob();
      downloadBlobFile(blob, artifact.filename || 'whatquiz-file');
      trackChatUxEvent('share_file_download', { category, format: artifact.format, stage: 'success' });
    } catch (err) {
      setFileErrors((prev) => ({ ...prev, [artifact.file_id]: err.message || '파일을 받지 못했어요.' }));
      trackChatUxEvent('share_file_download', {
        category,
        format: artifact.format,
        stage: 'failed',
        errorCode: err.status ? `http_${err.status}` : 'network',
      });
    } finally {
      setDownloadingId(null);
    }
  }, [category, downloadingId]);

  const handleRevoke = useCallback(async () => {
    if (revoking) return;
    if (!window.confirm('이 공유 링크를 끌까요? 링크를 받은 사람은 더 이상 대화와 파일을 볼 수 없어요.')) return;
    setRevoking(true);
    try {
      await deleteShareLink(shareId);
      setRevoked(true);
      trackChatUxEvent('share_revoked', { category, entry: 'page' });
    } catch (err) {
      setNotice({ type: 'error', text: '공유를 끄지 못했어요. 다시 시도해주세요.' });
    } finally {
      setRevoking(false);
    }
  }, [category, revoking, shareId]);

  if (loading) {
    return (
      <div className="shared-chat-page">
        <ShareTopbar linkHome={false} />
        <main className="shared-chat-main" aria-label="대화를 불러오는 중">
          <div className="shared-chat-summary-skeleton">
            <span />
            <span />
            <span />
          </div>
          <div className="shared-chat-message-skeleton is-user" />
          <div className="shared-chat-message-skeleton is-assistant" />
          <div className="shared-chat-message-skeleton is-user" />
        </main>
      </div>
    );
  }

  if (error || revoked) {
    return (
      <div className="shared-chat-page">
        <ShareTopbar />
        <main className="shared-chat-error">
          <div className="shared-chat-error-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 9v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M12 17h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            </svg>
          </div>
          <h2>{revoked ? '공유를 껐어요' : '대화를 찾을 수 없습니다'}</h2>
          <p>{revoked ? '이제 이 링크로는 대화와 파일을 볼 수 없어요.' : error}</p>
          <Link className="shared-chat-cta-button" to={revoked ? '/chat' : '/'}>
            {revoked ? 'AI 대화로 돌아가기' : '왓퀴즈 둘러보기'}
          </Link>
        </main>
      </div>
    );
  }

  const categoryName = sharedData?.category_name || CATEGORY_NAMES[category] || 'AI 대화';
  const tryUrl = buildShareTryUrl(sharedData);

  return (
    <div className="shared-chat-page">
      <Helmet>
        <title>{sharedData?.title || '공유받은 AI 대화'} | 왓퀴즈</title>
        <meta
          name="description"
          content="동료 선생님이 왓퀴즈에서 나눈 AI 대화를 확인해보세요."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <ShareTopbar />

      <main className="shared-chat-main">
        <section className="shared-chat-info" aria-labelledby="shared-chat-title">
          <span className="shared-chat-eyebrow">공유받은 대화</span>
          <h1 className="shared-chat-title" id="shared-chat-title">
            {sharedData?.title || '왓퀴즈 AI와 나눈 대화'}
          </h1>
          <div className="shared-chat-meta">
            <span>{categoryName}</span>
            <span aria-hidden="true">·</span>
            <span>메시지 {messages.length}개</span>
            {fileSummary.generated > 0 && (
              <>
                <span aria-hidden="true">·</span>
                <span>파일 {fileSummary.generated}개</span>
              </>
            )}
            {sharedData?.created_at && (
              <>
                <span aria-hidden="true">·</span>
                <time dateTime={sharedData.created_at}>
                  {formatTime(sharedData.created_at)}
                </time>
              </>
            )}
          </div>
          <p className="shared-chat-readonly-note">
            {fileSummary.generated > 0
              ? 'AI가 만든 파일은 미리보고 저장할 수 있어요.'
              : '이 대화는 읽기 전용이에요.'}
          </p>
          {sharedData?.can_manage && (
            <div className="shared-chat-owner-bar">
              <span>내가 공유한 대화예요</span>
              <button type="button" onClick={handleRevoke} disabled={revoking}>
                {revoking ? '끄는 중' : '공유 중지'}
              </button>
            </div>
          )}
        </section>

        {notice && (
          <div className={`shared-chat-notice is-${notice.type}`} role="alert">
            {notice.text}
          </div>
        )}

        <section className="shared-chat-messages" aria-label="공유된 메시지">
          {messages.map((message, index) => {
            const artifacts = Array.isArray(message.artifacts) ? message.artifacts : [];
            const attachmentNames = Array.isArray(message.attachment_names) ? message.attachment_names : [];
            return (
              <article
                key={`${message.role}-${message.timestamp || ''}-${index}`}
                className={`shared-message ${message.role === 'user' ? 'user' : 'assistant'}`}
              >
                <div className="shared-message-content">
                  <span className="shared-message-role">
                    {message.role === 'user' ? '선생님' : '왓퀴즈 AI'}
                  </span>
                  {attachmentNames.length > 0 && (
                    <div className="shared-attachments" aria-label="선생님이 올린 파일">
                      {attachmentNames.map((name, nameIndex) => (
                        <span className="shared-attachment-chip" key={`${nameIndex}-${name}`} title={name}>
                          <ClipIcon />
                          <span>{name}</span>
                        </span>
                      ))}
                      <span className="shared-attachments-note">올린 파일은 공유되지 않아요</span>
                    </div>
                  )}
                  <div className="shared-message-text">
                    {message.role === 'assistant' ? (
                      <EnhancedMarkdown
                        content={answerTextForShare(stripLeakedCitationMarkers(message.content), [])}
                        contentCategory={message.category}
                        enableMath={true}
                      />
                    ) : (
                      <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
                    )}
                  </div>
                  {artifacts.length > 0 && (
                    <div className="shared-file-list" aria-label="AI가 만든 파일">
                      {artifacts.map((artifact) => (
                        <SharedFileCard
                          artifact={artifact}
                          busy={downloadingId === artifact.file_id}
                          error={fileErrors[artifact.file_id]}
                          key={artifact.file_id}
                          onDownload={handleDownload}
                          onPreview={handlePreview}
                        />
                      ))}
                    </div>
                  )}
                  {message.timestamp && (
                    <time className="shared-message-time" dateTime={message.timestamp}>
                      {formatTime(message.timestamp)}
                    </time>
                  )}
                </div>
              </article>
            );
          })}
        </section>

        <section className="shared-chat-next">
          <div className="shared-chat-next-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            </svg>
          </div>
          <div className="shared-chat-next-copy">
            <h2>나도 이렇게 만들어 보기</h2>
            <p>
              {category === 'document'
                ? '내 양식을 올리면 같은 방식으로 한글 파일을 만들어 드려요.'
                : '같은 질문을 입력창에 넣어 드려요. 내 상황에 맞게 고쳐서 보내 보세요.'}
            </p>
          </div>
          <div className="shared-chat-next-actions">
            <Link
              to={tryUrl}
              className="shared-chat-primary-cta"
              onClick={() => trackChatUxEvent('share_try_click', { category, artifactCount: fileSummary.generated })}
            >
              같은 질문으로 시작하기
            </Link>
            <Link to="/" className="shared-chat-secondary-cta">
              왓퀴즈 둘러보기
            </Link>
          </div>
        </section>
      </main>

      {previewArtifact && (
        <div
          className="shared-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`${previewArtifact.filename} 미리보기`}
        >
          <Suspense fallback={<div className="shared-preview-boot">미리보기를 준비하고 있어요</div>}>
            <HwpDocResultViewer
              docId={previewArtifact.file_id}
              filename={previewArtifact.filename}
              fullscreen
              onClose={() => setPreviewArtifact(null)}
              onFallbackDownload={() => handleDownload(previewArtifact)}
              rawBytesPath={previewArtifact.preview_path}
              title={previewArtifact.filename}
              trackPreviewEvent={(action, meta) => {
                if (action === 'preview_failed') {
                  trackChatUxEvent('share_file_preview', {
                    category,
                    format: previewArtifact.format,
                    stage: 'failed',
                    errorCode: meta?.reason,
                  });
                }
              }}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
};

export default SharedChatPage;
